/**
 * SubscribeButton — one component for place / topic / author / publication
 * subscriptions. Idempotent — clicking twice does the right thing.
 * Anonymous users see a "Sign in to subscribe" tooltip.
 */
import { useCallback, useEffect, useState } from "react";
import { Bell, Check, Loader2 } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token");
}

export default function SubscribeButton({
  targetType, // 'place' | 'topic' | 'author' | 'publication'
  targetId,
  label = "Follow",
  size = "md",
  testId = "publisher-subscribe",
}) {
  const [state, setState] = useState({
    subscribed: false, id: null, loading: true, error: null,
  });

  const loadStatus = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setState({ subscribed: false, id: null, loading: false, error: null });
      return;
    }
    try {
      const r = await fetch(
        `${BASE}/subscribe/status?target_type=${encodeURIComponent(targetType)}&target_id=${encodeURIComponent(targetId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error();
      const d = await r.json();
      setState({ subscribed: d.subscribed, id: d.id, loading: false, error: null });
    } catch {
      setState({ subscribed: false, id: null, loading: false, error: null });
    }
  }, [targetType, targetId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const toggle = useCallback(async () => {
    const token = getToken();
    if (!token) {
      window.location.href = "/login?next=" + encodeURIComponent(window.location.pathname);
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      if (state.subscribed && state.id) {
        await fetch(`${BASE}/subscribe/${state.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        setState({ subscribed: false, id: null, loading: false, error: null });
      } else {
        const r = await fetch(`${BASE}/subscribe`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            target_type: targetType,
            target_id: targetId,
            delivery: "in_app",
          }),
        });
        const d = await r.json();
        setState({ subscribed: true, id: d.id, loading: false, error: null });
      }
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e.message }));
    }
  }, [state.subscribed, state.id, targetType, targetId]);

  const sizeCls = size === "sm"
    ? "px-2.5 py-1 text-xs"
    : "px-4 py-1.5 text-sm";

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={state.loading}
      aria-pressed={state.subscribed}
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors border ${sizeCls} ${
        state.subscribed
          ? "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-red-50 hover:border-red-200 hover:text-red-700"
          : "bg-slate-900 border-slate-900 text-white hover:bg-slate-700"
      } disabled:opacity-60`}
      data-testid={testId}
    >
      {state.loading ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : state.subscribed ? (
        <Check className="w-3 h-3" />
      ) : (
        <Bell className="w-3 h-3" />
      )}
      {state.subscribed ? "Following" : label}
    </button>
  );
}
